import { Scene, Vector3, AbstractMesh, Mesh, InstancedMesh, TransformNode } from '@babylonjs/core';
import type { Observer } from '@babylonjs/core/Misc/observable';
import { createActor } from 'xstate';
import { PhysicsManager, CollisionGroups, CollisionMasks, PhysicsShapeType } from './physics';
import { ActionHandlerRegistry, StateManager } from '../state';
import { dynamicObjectMachine } from '../state/machines/dynamicObjectMachine';
import { BehaviorRegistry } from '../core/BehaviorRegistry';
import { logger } from '../core/Logger';
import { EntityRegistry } from '../core/EntityRegistry';
import { EventBus } from '../core/EventBus';
import { ParticleManager } from './ParticleManager';
import type { DebrisImpactContext } from './particles/debrisEffects';
import { getWorldPosition, getWorldTransform, convertInstanceToMesh, centerMeshGeometry } from './utils/MeshTransformUtils';
import { 
  EntityType,
  type CollisionContext, 
  type CollisionActions, 
  type CollisionHookAPI,
  type GameServices,
  type ParticleSpawnOptions,
  type SoundPlayOptions
} from '../types/EntityTypes';

/**
 * Asset configuration for dynamic objects
 */
export interface AssetConfig {
  id: string;
  file: string;
  category: string;
  tags: string[];
  physics?: {
    defaultMass?: number;
    defaultFriction?: number;
    defaultRestitution?: number;
  };
  destructible?: {
    breakThreshold?: number;
    debrisLifetime?: number;
  };
}

/**
 * Instantiated asset result for dynamic objects
 */
export interface InstantiatedAsset {
  id: string;
  root: AbstractMesh;
  renderMeshes: AbstractMesh[];
  collisionMeshes: AbstractMesh[];
  debrisMeshes: AbstractMesh[];
  sockets: AbstractMesh[];
  config: AssetConfig;
}

/**
 * Dynamic object instance
 */
export interface DynamicObject {
  id: string;
  asset: InstantiatedAsset;
  actor: ReturnType<typeof createActor<typeof dynamicObjectMachine>>;
  physicsBodyId: string;
  behaviorId: string; // Behavior ID for BehaviorRegistry lookup (e.g., 'cube', 'crate_wooden')
}

/**
 * Context for object lifecycle hooks
 */
export interface ObjectHookContext {
  readonly objectId: string;
  readonly behaviorId: string;
  readonly impactPoint?: Vector3;
  readonly impactDirection?: Vector3;
  readonly impactForce?: number;
}

/**
 * Object lifecycle hooks
 */
export interface DynamicObjectHooks {
  onObjectSleep?: (ctx: ObjectHookContext) => void;
  onObjectWake?: (ctx: ObjectHookContext) => void;
  onObjectCollision?: (ctx: ObjectHookContext) => void;
  onObjectBreak?: (ctx: ObjectHookContext) => void;
}

/**
 * DynamicObjectSystem - Manages runtime instances of destructible objects
 */
export class DynamicObjectSystem {
  private objects: Map<string, DynamicObject> = new Map();
  private hooks: DynamicObjectHooks = {};
  private _scene: Scene;
  private physicsManager: PhysicsManager;
  private entityRegistry: EntityRegistry;
  private particleManager: ParticleManager | null = null;
  private eventBus: EventBus | null = null;
  private _stateManager: StateManager | null = null;
  private debrisFadeObservers: Map<string, Observer<Scene>[]> = new Map();
  /** Tracks which debris bodies have already emitted dust particles on first impact */
  private debrisDustEmitted: Set<string> = new Set();

  constructor(
    scene: Scene,
    physicsManager: PhysicsManager,
    entityRegistry: EntityRegistry,
    particleManager?: ParticleManager,
    eventBus?: EventBus
  ) {
    this._scene = scene;
    this.physicsManager = physicsManager;
    this.entityRegistry = entityRegistry;
    this.particleManager = particleManager ?? null;
    this.eventBus = eventBus ?? null;
  }

  /**
   * Initialize with state manager
   */
  initialize(stateManager?: StateManager): void {
    this._stateManager = stateManager || null;
    logger.core.log(`✅ DynamicObjectSystem initialized (state manager: ${this._stateManager ? 'attached' : 'none'})`);

    // Subscribe to global physics collisions so dynamic objects can emit hooks/events
    ActionHandlerRegistry.registerPhysicsHooks({
      onCollisionStart: (bodyAId, bodyBId, pairKey) => {
        this.handlePhysicsCollision('start', bodyAId, bodyBId, pairKey);
        this.handlePhysicsCollision('start', bodyBId, bodyAId, pairKey);
      },
      onCollisionEnd: (bodyAId, bodyBId, pairKey) => {
        this.handlePhysicsCollision('end', bodyAId, bodyBId, pairKey);
        this.handlePhysicsCollision('end', bodyBId, bodyAId, pairKey);
      }
    });
  }

  /**
   * Set lifecycle hooks
   */
  setHooks(hooks: DynamicObjectHooks): void {
    this.hooks = hooks;
  }

  /**
   * Register a map-based asset that was already created by LevelBuilder.
   * This allows collision hooks and other DynamicObjectSystem features to work
   * with assets defined directly in the map file.
   * 
   * @param objectId - The object ID (e.g., 'asset_asset_cube')
   * @param physicsBodyId - The physics body ID (same as objectId for LevelBuilder assets)
   * @param behaviorId - The behavior ID for BehaviorRegistry lookup (e.g., 'cube')
   * @param physicsMesh - The mesh with the physics body attached
   * @param actor - The XState actor for this object (optional - for manual state management)
   */
  registerMapAsset(
    objectId: string,
    physicsBodyId: string,
    behaviorId: string,
    physicsMesh: AbstractMesh,
    actor?: ReturnType<typeof createActor<typeof dynamicObjectMachine>>
  ): void {
    // Get render mesh from metadata (set by LevelBuilder) or fall back to physics mesh
    const renderMesh = physicsMesh.metadata?._renderMesh as AbstractMesh | undefined ?? physicsMesh;
    const debrisMeshes = (physicsMesh.metadata?._debrisMeshes as AbstractMesh[]) ?? [];
    // For multi-material meshes, also get the render TransformNode
    const renderTransformNode = physicsMesh.metadata?._renderTransformNode as TransformNode | undefined;
    
    // Build renderMeshes array - include TransformNode children if present
    const renderMeshes: AbstractMesh[] = [];
    if (renderTransformNode) {
      // Multi-material mesh: get all child meshes from the TransformNode
      const childMeshes = renderTransformNode.getChildMeshes(true);
      renderMeshes.push(...childMeshes);
      logger.core.log(`[DynamicObjectSystem] Multi-material render: ${renderTransformNode.name} with ${childMeshes.length} primitives`);
    } else if (renderMesh) {
      renderMeshes.push(renderMesh);
    }
    
    // Create a minimal InstantiatedAsset wrapper for the mesh
    const asset: InstantiatedAsset = {
      id: behaviorId,
      root: physicsMesh,
      renderMeshes,
      collisionMeshes: [physicsMesh],
      debrisMeshes,
      sockets: [],
      // Store the TransformNode for hiding during destruction
      _renderTransformNode: renderTransformNode,
      config: {
        id: behaviorId,
        file: '',
        category: 'map_asset',
        tags: [],
        physics: {
          defaultMass: 10,
          defaultFriction: 0.6,
          defaultRestitution: 0.2
        },
        destructible: {
          breakThreshold: 100,
          debrisLifetime: 10
        }
      }
    } as InstantiatedAsset & { _renderTransformNode?: TransformNode };

    // If no actor provided, create one using the behavior's machine or fallback to default
    if (!actor) {
      // Try to get machine from behavior registry
      const { behavior } = BehaviorRegistry.get(behaviorId);
      const machine = behavior?.createMachine?.() ?? dynamicObjectMachine;
      
      // Use AssetContext-compatible input for asset machines (they use 'id' not 'objectId')
      const machineInput = {
        id: objectId,
        objectId,
        assetId: behaviorId,
        behaviorId,
        health: 10,
        maxHealth: 10,
        totalDamage: 0,
        accumulatedDamage: 0,
        breakThreshold: 100,
        debrisLifetime: 10,
        debrisTimer: 0
      };
      
      actor = createActor(machine, { input: machineInput }) as ReturnType<typeof createActor<typeof dynamicObjectMachine>>;
      actor.start();
      
      // Register with StateManager for debugging visibility
      if (this._stateManager) {
        this._stateManager.registerDynamicObject(objectId, actor as any);
      }
    }
    
    // Subscribe to state changes - CRITICAL for breaking/debris lifecycle
    // This handles hiding render mesh, spawning debris, cleanup etc.
    actor.subscribe((state) => {
      this.handleStateChange(objectId, state.value as string);
    });

    const dynamicObject: DynamicObject = {
      id: objectId,
      asset,
      actor,
      physicsBodyId,
      behaviorId
    };

    this.objects.set(objectId, dynamicObject);
    
    // Register with EntityRegistry for type-safe collision lookups
    const entityInfo = this.entityRegistry.registerEntityInfo({
      id: objectId,
      physicsBodyId,
      type: EntityType.DYNAMIC_OBJECT,
      behaviorId,
      isPlayer: false,
      actor,
      mesh: asset.root,
      displayName: `${behaviorId} (${objectId})`,
    });
    
    // Call onSpawn hook for the behavior
    const { behavior } = BehaviorRegistry.get(behaviorId);
    if (behavior?.onSpawn) {
      try {
        const spawnApi = this.buildSpawnHookAPI(entityInfo, physicsMesh.position.clone());
        behavior.onSpawn(spawnApi);
      } catch (error) {
        logger.core.error(`Error in behavior spawn hook for ${behaviorId}:`, error);
      }
    }
    
    logger.core.log(`Registered map asset: ${objectId} (behaviorId: ${behaviorId}, physicsBodyId: ${physicsBodyId})`);
  }

  /**
   * Handle state machine transitions
   */
  private handleStateChange(objectId: string, newState: string): void {
    const obj = this.objects.get(objectId);
    if (!obj) return;

    logger.core.debug(`Object ${objectId} state: ${newState}`);

    switch (newState) {
      case 'breaking':
        this.handleBreaking(obj);
        break;
      case 'debris':
        this.handleDebris(obj);
        break;
      case 'cleanup':
        this.handleCleanup(objectId);
        break;
    }
  }

  /**
   * Handle breaking state
   */
  private handleBreaking(obj: DynamicObject): void {
    logger.core.log(`Object ${obj.id} is breaking!`);
    ActionHandlerRegistry.onObjectBreak(obj.id, obj.behaviorId);
    
    // Get position for effects (using utility that works for all mesh types)
    const position = getWorldPosition(obj.asset.root as AbstractMesh);
    
    // Call behavior's onBreak hook
    const { behavior, resolvedId } = BehaviorRegistry.get(obj.behaviorId);
    logger.core.log(`onBreak lookup: obj.behaviorId='${obj.behaviorId}', resolvedId='${resolvedId}', hasOnBreak=${!!behavior?.onBreak}`);
    if (behavior?.onBreak) {
      const entityInfo = this.entityRegistry.getEntityInfo(obj.id);
      logger.core.log(`onBreak entityInfo: ${entityInfo ? entityInfo.id : 'null'}`);
      if (entityInfo) {
        const api = this.buildBreakHookAPI(entityInfo, position);
        try {
          behavior.onBreak(api);
        } catch (error) {
          logger.core.error('Error in behavior onBreak hook:', error);
        }
      }
    }
    
    // Call legacy hook
    if (this.hooks.onObjectBreak) {
      const ctx: ObjectHookContext = {
        objectId: obj.id,
        behaviorId: obj.behaviorId
      };
      
      try {
        this.hooks.onObjectBreak(ctx);
      } catch (error) {
        logger.core.error('Error in onObjectBreak hook:', error);
      }
    }
  }

  /**
   * Handle debris state - spawn debris pieces
   */
  private handleDebris(obj: DynamicObject): void {
    logger.core.log(`Object ${obj.id} spawning debris`);
    
    // Hide intact render meshes and make them not pickable
    // CRITICAL: Must hide all descendants too, as render mesh may be a TransformNode
    // with actual mesh children (e.g., multi-material primitives)
    
    // First, check for a TransformNode parent (multi-material meshes)
    const assetWithTransformNode = obj.asset as InstantiatedAsset & { _renderTransformNode?: TransformNode };
    if (assetWithTransformNode._renderTransformNode) {
      const transformNode = assetWithTransformNode._renderTransformNode;
      logger.core.log(`  Hiding render TransformNode: ${transformNode.name}`);
      transformNode.setEnabled(false);
    }
    
    logger.core.log(`  Hiding ${obj.asset.renderMeshes.length} render meshes`);
    for (const mesh of obj.asset.renderMeshes) {
      logger.core.log(`    Hiding render mesh: ${mesh.name}, children: ${mesh.getChildMeshes(true).length}`);
      mesh.isVisible = false;
      mesh.isPickable = false;
      mesh.setEnabled(false); // Also disable the node to hide children
      // Hide all descendant meshes (handles TransformNodes with mesh children)
      for (const child of mesh.getChildMeshes(true)) {
        logger.core.log(`      Hiding child: ${child.name}`);
        child.isVisible = false;
        child.isPickable = false;
        child.setEnabled(false);
      }
    }

    // Disable intact collision meshes (make not pickable for raycasting)
    for (const mesh of obj.asset.collisionMeshes) {
      mesh.isPickable = false;
      mesh.isVisible = false;
      mesh.setEnabled(false);
    }

    // Unregister from damageable mesh registry so combat hits no longer target this object
    this._stateManager?.unregisterDamageableMesh(obj.id);

    // Disable intact collision physics body
    this.physicsManager.removeBody(obj.physicsBodyId);

    // Show and enable physics for debris pieces
    for (let i = 0; i < obj.asset.debrisMeshes.length; i++) {
      let debris = obj.asset.debrisMeshes[i];
      
      // Re-enable the mesh first so transforms can be computed
      debris.setEnabled(true);
      debris.isVisible = true;
      
      // Get world transform (works correctly for both Mesh and InstancedMesh)
      const { position: debrisWorldPos, rotation: debrisWorldRot, scale: debrisWorldScale } = getWorldTransform(debris);
      
      logger.core.debug(`   Debris ${i}: ${debris.name} at ${debrisWorldPos.toString()}`);
      
      // Handle InstancedMesh: convert to regular Mesh for physics (InstancedMesh can't have unique physics)
      if (debris instanceof InstancedMesh) {
        const converted = convertInstanceToMesh(debris, {
          centerGeometry: true,
          disposeInstance: true,
          suffix: '_debris'
        });
        if (converted) {
          obj.asset.debrisMeshes[i] = converted;
          debris = converted;
        }
      } else if (debris instanceof Mesh) {
        // Unparent from physics mesh so debris can fly independently
        debris.setParent(null);
        
        // Apply world transform
        debris.position.copyFrom(debrisWorldPos);
        debris.rotationQuaternion = debrisWorldRot.clone();
        debris.scaling.copyFrom(debrisWorldScale);
        debris.computeWorldMatrix(true);
        
        // Center geometry for physics
        centerMeshGeometry(debris);
      }

      // Create physics body for debris
      // Use BOX impostor for performance - CONVEX_HULL is expensive for many debris pieces
      const debrisBodyId = `${obj.physicsBodyId}_debris_${i}`;
      this.physicsManager.createBody(
        debrisBodyId,
        debris,
        PhysicsShapeType.BOX,
        {
          mass: 1.0, // TODO: Read from hm_debris_mass
          friction: 0.6,
          restitution: 0.3,
          collisionGroup: CollisionGroups.DYNAMIC,
          collisionMask: CollisionMasks.DYNAMIC
        }
      );

      // Apply explosion impulse
      const body = this.physicsManager.getBody(debrisBodyId);
      if (body) {
        const randomImpulse = new Vector3(
          (Math.random() - 0.5) * 10,
          Math.random() * 5 + 5,
          (Math.random() - 0.5) * 10
        );
        body.applyImpulse(randomImpulse);
      }
    }

    // Start fade-out timers for debris
    const debrisLifetime = obj.asset.config.destructible?.debrisLifetime ?? 10;
    this.startDebrisFade(obj.id, obj.asset.debrisMeshes, debrisLifetime);

    // Schedule cleanup after lifetime
    setTimeout(() => {
      obj.actor.send({ type: 'DEBRIS_TIMEOUT' });
    }, debrisLifetime * 1000);
  }

  /**
   * Handle cleanup state
   */
  private handleCleanup(objectId: string): void {
    const obj = this.objects.get(objectId);
    if (!obj) return;

    logger.core.log(`Cleaning up object: ${objectId}`);

    // Remove all debris physics bodies and clean up dust tracking
    for (let i = 0; i < obj.asset.debrisMeshes.length; i++) {
      const debrisBodyId = `${obj.physicsBodyId}_debris_${i}`;
      this.physicsManager.removeBody(debrisBodyId);
      this.debrisDustEmitted.delete(debrisBodyId);
      
      // Explicitly dispose debris meshes - they may have been converted from InstancedMesh
      // and are no longer children of obj.asset.root
      const debrisMesh = obj.asset.debrisMeshes[i];
      if (debrisMesh && !debrisMesh.isDisposed()) {
        debrisMesh.dispose();
      }
    }

    // Stop fade observers
    this.clearDebrisFaders(objectId);

    // Dispose root and remaining children (render meshes, etc.)
    obj.asset.root.dispose();

    // Stop actor
    obj.actor.stop();

    // Unregister from StateManager
    this._stateManager?.unregisterDynamicObject(objectId);

    // Unregister from EntityRegistry
    this.entityRegistry.unregisterEntityInfo(objectId);

    // Remove from registry
    this.objects.delete(objectId);
  }

  /**
   * Apply damage to an object
   */
  applyDamage(
    objectId: string,
    amount: number,
    impactForce: number = 10,
    impactDirection: Vector3 = Vector3.Up()
  ): void {
    const obj = this.objects.get(objectId);
    if (!obj) return;

    obj.actor.send({
      type: 'DAMAGE_RECEIVED',
      amount,
      impactForce,
      impactDirection: [impactDirection.x, impactDirection.y, impactDirection.z]
    });
  }

  /**
   * Route physics collision events to hooks/XState
   */
  private startDebrisFade(objectId: string, meshes: AbstractMesh[], lifetimeSeconds: number): void {
    if (lifetimeSeconds <= 0) return;

    const fadeDuration = Math.min(2, Math.max(0.5, lifetimeSeconds * 0.25));
    const fadeStartMs = performance.now() + Math.max(0, (lifetimeSeconds - fadeDuration) * 1000);
    const fadeEndMs = performance.now() + lifetimeSeconds * 1000;

    const observers: Observer<Scene>[] = [];

    for (const mesh of meshes) {
      const observer = this._scene.onBeforeRenderObservable.add(() => {
        const now = performance.now();
        if (now >= fadeEndMs) {
          mesh.visibility = 0;
          this._scene.onBeforeRenderObservable.remove(observer);
          return;
        }

        if (now >= fadeStartMs) {
          const t = (now - fadeStartMs) / (fadeEndMs - fadeStartMs);
          mesh.visibility = Math.max(0, 1 - t);
        }
      });

      if (observer) {
        observers.push(observer);
      }
    }

    if (observers.length > 0) {
      this.debrisFadeObservers.set(objectId, observers);
    }
  }

  private clearDebrisFaders(objectId: string): void {
    const observers = this.debrisFadeObservers.get(objectId);
    if (!observers) return;
    for (const observer of observers) {
      this._scene.onBeforeRenderObservable.remove(observer);
    }
    this.debrisFadeObservers.delete(objectId);
  }

  /**
   * Route physics collision events to hooks/XState
   */
  private handlePhysicsCollision(
    kind: 'start' | 'end',
    bodyId?: string,
    otherBodyId?: string,
    pairKey?: string
  ): void {
    if (kind === 'end') return; // Only fire hooks on collision start for now
    
    // Check for debris-to-static collision to spawn dust particles
    if (bodyId && this.isDebrisBodyId(bodyId)) {
      this.handleDebrisImpact(bodyId, otherBodyId);
      return; // Debris doesn't trigger regular collision hooks
    }
    
    const objectId = this.resolveObjectId(bodyId);
    if (!objectId) return;

    const obj = this.objects.get(objectId);
    if (!obj) return;

    // Calculate collision impulse and relative velocity
    let impactSpeed = 0;
    let relativeVelocity = Vector3.Zero();
    const objectBody = this.physicsManager.getBody(bodyId || '');
    const otherBody = otherBodyId ? this.physicsManager.getBody(otherBodyId) : null;
    
    if (objectBody && otherBody) {
      const objectVel = objectBody.getLinearVelocity();
      const otherVel = otherBody.getLinearVelocity();
      relativeVelocity = otherVel.subtract(objectVel);
      impactSpeed = relativeVelocity.length();
    }

        // Dispatch behavior-specific collision hook
    const { behavior } = BehaviorRegistry.get(obj.behaviorId);
    
    // Call collision hook (unified API)
    if (behavior?.onCollision) {
      try {
        const api = this.buildCollisionHookAPI(obj, objectId, bodyId || '', otherBodyId || '', impactSpeed, relativeVelocity);
        behavior.onCollision(api);
      } catch (error) {
        logger.core.error('Error in behavior collision hook:', error);
      }
    }

    // Dispatch global hooks
    if (this.hooks.onObjectCollision) {
      const ctx: ObjectHookContext = {
        objectId,
        behaviorId: obj.behaviorId,
        impactForce: impactSpeed
      };
      try {
        this.hooks.onObjectCollision(ctx);
      } catch (error) {
        logger.core.error('Error in onObjectCollision hook:', error);
      }
    }

    ActionHandlerRegistry.onObjectCollision(objectId, obj.asset.id, otherBodyId, pairKey);
  }

  /**
   * Build the CollisionHookAPI for new-style hooks
   */
  private buildCollisionHookAPI(
    obj: DynamicObject,
    objectId: string,
    bodyId: string,
    otherBodyId: string,
    impactSpeed: number,
    relativeVelocity: Vector3
  ): CollisionHookAPI {
    // Get entity info for both participants
    const selfInfo = this.entityRegistry.getEntityInfoByPhysicsIdOrUnknown(bodyId);
    const otherInfo = this.entityRegistry.getEntityInfoByPhysicsIdOrUnknown(otherBodyId);
    
    // Build collision context
    const ctx: CollisionContext = {
      self: selfInfo,
      other: otherInfo,
      impulse: impactSpeed,
      contactPoint: Vector3.Zero(), // TODO: Extract from Ammo.js manifold
      contactNormal: Vector3.Up(), // TODO: Extract from Ammo.js manifold
      relativeVelocity,
    };
    
    // Build actions object (captures objectId and obj for closure)
    const actions = this.buildCollisionActions(objectId, obj, otherBodyId, impactSpeed);
    
    // Build services
    const services = this.buildGameServices();
    
    return { ctx, actions, services };
  }

  /**
   * Build the CollisionActions for new-style hooks
   */
  private buildCollisionActions(
    objectId: string, 
    obj: DynamicObject,
    otherBodyId: string,
    impactSpeed: number
  ): CollisionActions {
    const self = this;
    const otherObj = this.resolveObjectId(otherBodyId);
    
    return {
      // === Damage ===
      applyDamage(amount: number): void {
        logger.physics.log(`[Collision Damage] ${objectId} took ${amount.toFixed(1)} damage via hook API`);
        self.applyDamage(objectId, amount, impactSpeed, Vector3.Up());
      },
      
      applyDamageToOther(amount: number): void {
        if (otherObj) {
          logger.physics.log(`[Collision Damage] ${otherObj} took ${amount.toFixed(1)} damage via hook API (from ${objectId})`);
          self.applyDamage(otherObj, amount, impactSpeed, Vector3.Up());
        } else {
          // Try to apply damage to character via CharacterController
          // TODO: Wire this up once CharacterController is integrated
          logger.physics.debug(`Cannot apply damage to non-object: ${otherBodyId}`);
        }
      },
      
      // === Physics ===
      applyForce(direction: Vector3, magnitude: number): void {
        const body = self.physicsManager.getBody(obj.physicsBodyId);
        if (body) {
          const impulse = direction.normalize().scale(magnitude);
          body.applyImpulse(impulse, Vector3.Zero());
        }
      },
      
      applyForceToOther(direction: Vector3, magnitude: number): void {
        const body = self.physicsManager.getBody(otherBodyId);
        if (body) {
          const impulse = direction.normalize().scale(magnitude);
          body.applyImpulse(impulse, Vector3.Zero());
        }
      },
      
      // === Effects ===
      spawnParticles(preset: string, options?: ParticleSpawnOptions): void {
        if (!self.particleManager) {
          logger.core.warn(`Cannot spawn particles: ParticleManager not available`);
          return;
        }
        
        // Default position is the object's current position
        let position = obj.asset.root.position.clone();
        
        // Use contact point if available, or options position
        if (options?.position) {
          position = options.position;
        }
        
        // Spawn the particles
        self.particleManager.spawnOneShot(preset, position, {
          countMultiplier: options?.count ? options.count / 10 : undefined,  // Rough scaling
          scale: options?.scale,
          direction: options?.direction,
        });
      },
      
      playSound(_soundId: string, _options?: SoundPlayOptions): void {
        // TODO: Implement audio system integration
        logger.core.debug(`TODO: playSound(${_soundId}) for ${objectId}`);
      },
      
      // === State ===
      destroy(): void {
        self.handleCleanup(objectId);
      },
      
      sendEvent(eventType: string, payload?: Record<string, unknown>): void {
        const event = { type: eventType, ...payload } as any;
        obj.actor.send(event);
      },
      
      getHealth(): number {
        const snapshot = obj.actor.getSnapshot();
        return snapshot.context.health ?? 100;
      },
      
      getState(): string {
        const snapshot = obj.actor.getSnapshot();
        return typeof snapshot.value === 'string' ? snapshot.value : JSON.stringify(snapshot.value);
      },
      
      getActor() {
        return obj.actor;
      },
      
      // === Other Entity ===
      sendEventToOther(eventType: string, payload?: Record<string, unknown>): void {
        if (otherObj) {
          const otherObjData = self.objects.get(otherObj);
          if (otherObjData) {
            const event = { type: eventType, ...payload } as any;
            otherObjData.actor.send(event);
          }
        }
        // TODO: Support sending events to characters via EntityRegistry
      },
      
      getOtherHealth(): number {
        if (otherObj) {
          const otherObjData = self.objects.get(otherObj);
          if (otherObjData) {
            const snapshot = otherObjData.actor.getSnapshot();
            return snapshot.context.health ?? 100;
          }
        }
        return 0;
      },
      
      getOtherState(): string {
        if (otherObj) {
          const otherObjData = self.objects.get(otherObj);
          if (otherObjData) {
            const snapshot = otherObjData.actor.getSnapshot();
            return typeof snapshot.value === 'string' ? snapshot.value : JSON.stringify(snapshot.value);
          }
        }
        return 'unknown';
      },
    };
  }

  /**
   * Build GameServices for hook access to game systems
   */
  private buildGameServices(): GameServices {
    const self = this;
    
    return {
      physics: {
        applyImpulse(bodyId: string, impulse: Vector3): void {
          const body = self.physicsManager.getBody(bodyId);
          if (body) {
            body.applyImpulse(impulse, Vector3.Zero());
          }
        },
        getVelocity(bodyId: string): Vector3 | null {
          const body = self.physicsManager.getBody(bodyId);
          return body ? body.getLinearVelocity() : null;
        },
      },
      particles: {
        spawn(preset: string, position: Vector3, options?: ParticleSpawnOptions): void {
          if (!self.particleManager) {
            logger.core.warn(`Cannot spawn particles: ParticleManager not available`);
            return;
          }
          self.particleManager.spawnOneShot(preset, position, {
            countMultiplier: options?.count ? options.count / 10 : undefined,
            scale: options?.scale,
            direction: options?.direction,
          });
        },
      },
      audio: {
        play(_soundId: string, _options?: SoundPlayOptions): void {
          // TODO: Implement audio system
          logger.core.debug(`TODO: audio.play(${_soundId})`);
        },
      },
      state: {
        getActor(entityId: string) {
          const info = self.entityRegistry.getEntityInfo(entityId);
          return info?.actor;
        },
        sendEvent(entityId: string, event: { type: string; [key: string]: unknown }): void {
          const info = self.entityRegistry.getEntityInfo(entityId);
          if (info?.actor) {
            info.actor.send(event);
          }
        },
      },
    };
  }

  /**
   * Build SpawnHookAPI for onSpawn hooks
   */
  private buildSpawnHookAPI(entity: import('../types/EntityTypes').EntityInfo, position: Vector3): import('../types/EntityTypes').SpawnHookAPI {
    const services = this.buildGameServices();
    const self = this;
    
    return {
      self: entity,
      config: {},
      position,
      actions: {
        spawnParticles: (preset, options) => {
          if (!self.particleManager) {
            logger.core.warn(`Cannot spawn particles: ParticleManager not available`);
            return;
          }
          self.particleManager.spawnOneShot(preset, position, {
            countMultiplier: options?.count ? options.count / 10 : undefined,
            scale: options?.scale,
            direction: options?.direction,
          });
        },
        playSound: (_soundId, _options) => {
          logger.core.debug(`TODO: playSound(${_soundId}) for ${entity.id}`);
        },
        sendEvent: (eventType, payload) => {
          if (entity.actor) {
            entity.actor.send({ type: eventType, ...payload });
          }
        },
        getActor: () => entity.actor,
      },
      services,
    };
  }

  /**
   * Build BreakHookAPI for onBreak hooks
   */
  private buildBreakHookAPI(entity: import('../types/EntityTypes').EntityInfo, position: Vector3): import('../types/EntityTypes').BreakHookAPI {
    const services = this.buildGameServices();
    const self = this;
    
    return {
      self: entity,
      position,
      actions: {
        spawnParticles: (preset, options) => {
          const pos = options?.position || position;
          if (!self.particleManager) {
            logger.core.warn(`Cannot spawn particles: ParticleManager not available`);
            return;
          }
          self.particleManager.spawnOneShot(preset, pos, {
            countMultiplier: options?.count ? options.count / 10 : undefined,
            scale: options?.scale,
            direction: options?.direction,
          });
        },
        playSound: (_soundId, _options) => {
          logger.core.debug(`TODO: playSound(${_soundId}) for ${entity.id}`);
        },
        triggerImpactEffect: (preset, intensity = 1.0) => {
          if (!self.eventBus) {
            logger.core.warn(`Cannot trigger impact effect: EventBus not available`);
            return;
          }
          // Map preset names to event data
          const presetMap: Record<string, string> = {
            'small': 'smallImpact',
            'medium': 'mediumExplosion',
            'large': 'largeExplosion',
          };
          self.eventBus.emit('impact:explosion', {
            position,
            preset: presetMap[preset] ?? preset,
            intensity,
          });
        },
      },
      services,
    };
  }

  private resolveObjectId(bodyId?: string): string | null {
    if (!bodyId) return null;
    
    // Handle DynamicObjectSystem-created objects: physics_obj_N → obj_N
    if (bodyId.startsWith('physics_obj_')) {
      // Strip prefix and optional debris suffix
      const withoutPrefix = bodyId.replace(/^physics_/, '');
      const debrisIndex = withoutPrefix.indexOf('_debris_');
      if (debrisIndex !== -1) {
        return withoutPrefix.substring(0, debrisIndex);
      }
      return withoutPrefix;
    }
    
    // Handle LevelBuilder-created assets: asset_* → bodyId is the objectId
    // For these, the physics body ID equals the object ID
    if (bodyId.startsWith('asset_')) {
      return bodyId;
    }
    
    return null;
  }

  /**
   * Check if a body ID corresponds to a debris piece
   */
  private isDebrisBodyId(bodyId: string): boolean {
    return bodyId.includes('_debris_');
  }

  /**
   * Check if a body ID corresponds to static geometry (floor, walls, etc)
   * Uses EntityRegistry to check entity type instead of name patterns.
   */
  private isStaticBodyId(bodyId?: string): boolean {
    if (!bodyId) return false;
    
    // Check EntityRegistry for the entity type
    const entityInfo = this.entityRegistry.getEntityInfoByPhysicsId(bodyId);
    if (entityInfo) {
      return entityInfo.type === EntityType.STATIC_GEOMETRY;
    }
    
    // Fallback: check if body exists but isn't registered (legacy static geometry)
    // These would be meshes with hm_collision that weren't registered as entities
    const body = this.physicsManager.getBody(bodyId);
    if (body && body.mesh) {
      // Check for static collision metadata
      const hasCollisionMeta = body.mesh.metadata?.gltf?.extras?.hm_collision === true ||
                               body.mesh.metadata?.hm_collision === true;
      // Static if it has collision metadata but no entity registration (old-style level geometry)
      if (hasCollisionMeta && !entityInfo) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Extract parent object ID from debris body ID
   * Debris body IDs follow the pattern: {parentObjectId}_debris_{index}
   */
  private getDebrisParentObjectId(debrisBodyId: string): string | null {
    const debrisIndex = debrisBodyId.lastIndexOf('_debris_');
    if (debrisIndex === -1) return null;
    return debrisBodyId.substring(0, debrisIndex);
  }

  /**
   * Extract debris piece index from body ID
   */
  private getDebrisIndex(debrisBodyId: string): number {
    const match = debrisBodyId.match(/_debris_(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  }

  /**
   * Handle debris piece impact - spawn particles on first floor contact
   */
  private handleDebrisImpact(debrisBodyId: string, otherBodyId?: string): void {
    // Only spawn dust on first impact with static geometry
    if (!this.isStaticBodyId(otherBodyId)) return;
    
    // Check if we've already spawned dust for this debris
    if (this.debrisDustEmitted.has(debrisBodyId)) return;
    
    // Mark as emitted
    this.debrisDustEmitted.add(debrisBodyId);
    
    // Get debris position from physics body mesh (using utility for correct InstancedMesh handling)
    const body = this.physicsManager.getBody(debrisBodyId);
    if (!body || !this.particleManager) return;
    
    const position = getWorldPosition(body.mesh);
    
    // Look up the parent object to check debris particle hook
    const parentObjectId = this.getDebrisParentObjectId(debrisBodyId);
    const parentObject = parentObjectId ? this.objects.get(parentObjectId) : null;
    
    // Build impact context
    const impactContext: DebrisImpactContext = {
      position,
      debrisBodyId,
      parentObjectId,
      debrisIndex: this.getDebrisIndex(debrisBodyId),
    };
    
    // Get particle config from behavior hook or use default
    if (parentObject) {
      const { behavior } = BehaviorRegistry.get(parentObject.behaviorId);
      
      if (behavior.debrisParticle) {
        // Call the hook to get particle config
        const result = behavior.debrisParticle(impactContext);
        
        if (result === false) {
          // Hook explicitly disabled particles
          return;
        }
        
        if (result) {
          // Hook returned particle config
          const { preset, scale = 0.5, countMultiplier } = result;
          
          if (typeof preset === 'string') {
            // Use named preset
            this.particleManager.spawnOneShot(preset, position, { scale, countMultiplier });
          } else {
            // Register inline preset and spawn
            this.particleManager.registerPreset(preset);
            this.particleManager.spawnOneShot(preset.name, position, { scale, countMultiplier });
          }
          return;
        }
      }
    }
    
    // Default: spawn dust_puff
    this.particleManager.spawnOneShot('dust_puff', position, {
      scale: 0.5,
    });
  }

  /**
   * Get object count
   */
  getObjectCount(): number {
    return this.objects.size;
  }

  /**
   * Update all objects
   */
  update(_deltaTime: number): void {
    // Update logic here if needed
    // For now, state machine handles most lifecycle
  }

  /**
   * Get all active objects
   */
  getAllObjects(): DynamicObject[] {
    return Array.from(this.objects.values());
  }

  /**
   * Clear all objects
   */
  clear(): void {
    for (const objectId of Array.from(this.objects.keys())) {
      this.handleCleanup(objectId);
    }
  }

  /**
   * Dispose system
   */
  dispose(): void {
    this.clear();
    this.hooks = {};
    logger.core.debug('DynamicObjectSystem disposed');
  }
}
